import { Dispatch, useEffect, useMemo, useState, useCallback } from "react";
import { RadioButton, Label, Checkbox, Banner, LegacyCard, Tooltip, Icon, Collapsible } from "@shopify/polaris";
import styles from "./AllowedProductPages.module.css";
import {
    ProductObj,
    Action,
    StandardConfigInterface,
    BxgyConfigInterface,
    MixMatchConfigInterface,
    FBTConfigInterface,
    ProductAddonsConfigInterface,
    BuyNConfigInterface,
} from "../../apps/Bundles/Types";
import { Field } from "@shopify/react-form";
import { InfoIcon } from "@shopify/polaris-icons";
import { BundleTypes } from "../../apps/Bundles/constants";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import CollectionProductSelector from "../CollectionProductSelector/CollectionProductSelector";

interface AllowedProductPagesProps {
    validProductPages: Field<string[]> | null; // Change to string ar
    // ray for product IDs
    products: ProductObj[];
    dispatchBundleConfig:
        | Dispatch<Action<StandardConfigInterface>>
        | Dispatch<Action<MixMatchConfigInterface>>
        | Dispatch<Action<BxgyConfigInterface>>
        | Dispatch<Action<FBTConfigInterface>>
        | Dispatch<Action<ProductAddonsConfigInterface>>
        | Dispatch<Action<BuyNConfigInterface>>;
    updateKey: any;
    render_product_pages_type: any;
    show_on_no_pages?: Field<boolean>;
    dependencyArr: any[];
    mixmatch_product_selection?: Field<"all" | "specific" | "collection"> | null;
    collections?: {
        id: string;
        image?: {
            originalSrc?: string;
        };
        title: string;
    }[];
    MNMsectional?: boolean;
    bundleType: string;
    render_on_selected_collection_products?: Field<boolean>;
    allowed_collection_product_pages?: Field<ProductObj[]> | null;
}

const AllowedProductPages = ({
    validProductPages = null,
    products,
    dispatchBundleConfig,
    updateKey,
    render_product_pages_type,
    show_on_no_pages,
    dependencyArr,
    mixmatch_product_selection,
    collections = [],
    MNMsectional = false,
    bundleType,
    render_on_selected_collection_products,
    allowed_collection_product_pages = null,
}: AllowedProductPagesProps) => {
    const [allProductIds, setAllProductIds] = useState<string[] | []>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>(validProductPages?.value || []);
    const [selectedCollectionProducts, setSelectedCollectionProducts] = useState<ProductObj[]>([]);
    const { trackFormSections } = useBundleMixpanelHook();
    const [expandVisibility, setExpandVisibility] = useState(true);

    const isBXGY = bundleType === BundleTypes.BXGY;

    // Memoize validProductPages to prevent unnecessary re-renders
    const memoizedValidProductPages = useMemo(() => validProductPages?.value, [validProductPages?.value]);
    const memoizedAllowedCollectionProductPages = useMemo(
        () => allowed_collection_product_pages?.value,
        [allowed_collection_product_pages?.value],
    );

    // To avoid infinite loop of re-renders, we prevent unnecessary updates to selectedProductIds
    const updateSelectedProductIds = useCallback(
        (newIds: string[]) => {
            if (JSON.stringify(newIds) !== JSON.stringify(selectedProductIds)) {
                setSelectedProductIds(newIds);
                dispatchBundleConfig({
                    payload: newIds,
                    key: "allowed_product_pages",
                });
            }
        },
        [selectedProductIds, dispatchBundleConfig],
    );

    // Sync selectedCollectionProducts from allowed_collection_product_pages
    useEffect(() => {
        if (
            memoizedAllowedCollectionProductPages &&
            JSON.stringify(memoizedAllowedCollectionProductPages.map((p) => p.id)) !==
                JSON.stringify(selectedCollectionProducts.map((p) => p.id))
        ) {
            setSelectedCollectionProducts(memoizedAllowedCollectionProductPages);
        }

        if (memoizedAllowedCollectionProductPages?.length === 0 && selectedCollectionProducts.length > 0) {
            setSelectedCollectionProducts([]);
        }
    }, [memoizedAllowedCollectionProductPages]);

    useEffect(() => {
        const arr =
            memoizedValidProductPages?.filter(
                // @ts-expect-error hack
                (ps: string) => !!allProductIds.includes(ps),
            ) || [];
        updateSelectedProductIds(arr);
    }, [memoizedValidProductPages, allProductIds, updateSelectedProductIds]);

    useEffect(() => {
        if (products?.length >= 1) {
            const arr = Array.from(new Set(products.map((product: ProductObj) => product.id)));
            if (JSON.stringify(arr) !== JSON.stringify(allProductIds)) {
                setAllProductIds(arr);
            }
        } else if (allProductIds.length > 0) {
            // Prevent redundant updates
            dispatchBundleConfig({
                payload: [],
                key: "allowed_product_pages",
            });
            setAllProductIds([]);
        }
    }, [products, render_product_pages_type?.value, dependencyArr]);

    const updateArrayBasedOnCheckbox = (value?: string) => {
        if (!value) return;

        setSelectedProductIds((ps: string[]) => {
            const updatedIds = ps.includes(value) ? ps.filter((itm) => itm !== value) : [...ps, value];

            // Only dispatch if the array has changed
            if (JSON.stringify(updatedIds) !== JSON.stringify(ps)) {
                validProductPages?.onChange(updatedIds);
                dispatchBundleConfig({
                    payload: updatedIds,
                    key: "allowed_product_pages",
                });
            }

            return updatedIds;
        });
    };

    const handleCollectionProductsChange = (products: ProductObj[]) => {
        setSelectedCollectionProducts(products);

        allowed_collection_product_pages?.onChange(products);
        dispatchBundleConfig({
            payload: products,
            key: "allowed_collection_product_pages" as any,
        });
    };

    console._log("render_product_pages_type", render_product_pages_type.value);
    console._log("selectedProductIds", selectedProductIds);

    return (
        <>
            {isBXGY && (
                <div
                    className={styles.sectionTitleWrapper}
                    onClick={() =>
                        setExpandVisibility((ps) => {
                            trackFormSections({
                                cta: "Form Section",
                                ctaType: `Discount ${ps ? "Collapsed" : "Expanded"}`,
                                bundleType: bundleType,
                            });
                            return !ps;
                        })
                    }
                >
                    <div>Visibility</div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandVisibility ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>
            )}
            {!MNMsectional && (products?.length >= 1 || mixmatch_product_selection?.value === "all") && (
                <Collapsible
                    open={isBXGY ? expandVisibility : true}
                    id="basic-collapsible"
                    transition={{
                        duration: "500ms",
                        timingFunction: "ease-in-out",
                    }}
                    expandOnPrint
                >
                    {!MNMsectional && (products?.length >= 1 || mixmatch_product_selection?.value === "all") ? (
                        <>
                            <div
                                className={!isBXGY ? styles.card : undefined}
                                style={!isBXGY ? { marginTop: 10 } : undefined}
                            >
                                {/* Checkbox */}
                                <div className={styles.checkboxContainer}>
                                    <div className={styles.customLabelWrapper}>
                                        {!isBXGY && (
                                            <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                                                <Label id="bundle_product_level">Visibility</Label>
                                                <div style={{ marginTop: "5px" }}>Show bundle widget on</div>
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                marginTop: "8px",
                                                marginLeft: "10px",
                                                // gap: "4px",
                                            }}
                                        >
                                            <RadioButton
                                                label={"Included product pages"}
                                                id={"all pages"}
                                                checked={!render_product_pages_type.value && !show_on_no_pages?.value}
                                                name="bundle_position"
                                                onChange={() => {
                                                    dispatchBundleConfig({
                                                        payload: false,
                                                        key: "render_product_pages_type",
                                                    });
                                                    dispatchBundleConfig({
                                                        payload: [],
                                                        key: "allowed_product_pages",
                                                    });
                                                    render_product_pages_type.onChange(false);
                                                    validProductPages && validProductPages.onChange([]);
                                                    if (
                                                        bundleType == BundleTypes.STANDARD ||
                                                        (bundleType == BundleTypes.BUY_N &&
                                                            mixmatch_product_selection?.value == "specific") ||
                                                        (bundleType == BundleTypes.MIX_MATCH &&
                                                            mixmatch_product_selection?.value == "specific")
                                                    ) {
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "show_on_no_pages" as any,
                                                        });
                                                        show_on_no_pages?.onChange(false);
                                                    }
                                                }}
                                                disabled={mixmatch_product_selection?.value === "all"}
                                            />
                                            <RadioButton
                                                label={"Specific product pages"}
                                                id={"specific pages"}
                                                name="bundle_position"
                                                checked={render_product_pages_type.value && !show_on_no_pages?.value}
                                                onChange={() => {
                                                    dispatchBundleConfig({
                                                        payload: true,
                                                        key: "render_product_pages_type",
                                                    });
                                                    dispatchBundleConfig({
                                                        payload: [],
                                                        key: "allowed_product_pages",
                                                    });
                                                    render_product_pages_type.onChange(true);
                                                    validProductPages && validProductPages.onChange([]);
                                                    if (
                                                        bundleType == BundleTypes.STANDARD ||
                                                        (bundleType == BundleTypes.BUY_N &&
                                                            mixmatch_product_selection?.value == "specific") ||
                                                        (bundleType == BundleTypes.MIX_MATCH &&
                                                            mixmatch_product_selection?.value == "specific")
                                                    ) {
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "show_on_no_pages" as any,
                                                        });
                                                        show_on_no_pages?.onChange(false);
                                                    }
                                                }}
                                                disabled={mixmatch_product_selection?.value === "all"}
                                            />
                                            {!!render_product_pages_type.value &&
                                                validProductPages?.value?.length == 0 &&
                                                !show_on_no_pages?.value && (
                                                    <span
                                                        style={{
                                                            paddingLeft: 20,
                                                            display: "flex",
                                                            justifyContent: "stretch",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <LegacyCard title="">
                                                            <Banner tone={"info"}>
                                                                <p
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "stretch",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    Please select at least one product page
                                                                </p>
                                                            </Banner>
                                                        </LegacyCard>
                                                    </span>
                                                )}
                                            {mixmatch_product_selection?.value === "all" && (
                                                <RadioButton
                                                    label={
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            Do not show the widget on product pages
                                                            <div>
                                                                <Tooltip
                                                                    width="wide"
                                                                    content={
                                                                        "This bundle will not be rendered on any product page. To display it on another page, embed it via HTML. You can find the embed link in the Advanced tab."
                                                                    }
                                                                >
                                                                    <Icon source={InfoIcon} tone="subdued" />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    }
                                                    name="bundle_position"
                                                    checked={true}
                                                    onChange={() => {}}
                                                />
                                            )}
                                        </div>
                                        {render_product_pages_type.value == true && (
                                            <div className={styles.tagContainer} key={updateKey}>
                                                {Array.from(new Set(allProductIds)).map((id) => {
                                                    const product = products.find((product) => product.id === id);
                                                    return (
                                                        <Checkbox
                                                            onChange={() => {
                                                                updateArrayBasedOnCheckbox(product?.id);
                                                            }}
                                                            value={product?.id as string}
                                                            checked={
                                                                !!selectedProductIds.includes(product?.id as string)
                                                            }
                                                            label={product?.title || "Unknown Product"}
                                                            key={product?.id}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {(bundleType == BundleTypes.STANDARD ||
                                            (bundleType == BundleTypes.BUY_N &&
                                                mixmatch_product_selection?.value == "specific") ||
                                            (bundleType == BundleTypes.MIX_MATCH &&
                                                mixmatch_product_selection?.value == "specific")) && (
                                            <div style={{ marginLeft: "10px" }}>
                                                <RadioButton
                                                    label={"No product pages"}
                                                    id={"no pages"}
                                                    name="bundle_position"
                                                    checked={show_on_no_pages?.value}
                                                    onChange={() => {
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "render_product_pages_type",
                                                        });
                                                        dispatchBundleConfig({
                                                            payload: [],
                                                            key: "allowed_product_pages",
                                                        });
                                                        dispatchBundleConfig({
                                                            payload: true,
                                                            key: "show_on_no_pages" as any,
                                                        });
                                                        render_product_pages_type.onChange(false);
                                                        validProductPages && validProductPages.onChange([]);
                                                        show_on_no_pages?.onChange(true);
                                                    }}
                                                    // disabled={mixmatch_product_selection?.value === "all"}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </Collapsible>
            )}
            {/* Case when mixmatch_product_selection.value === "collection" and collections array length > 0 */}
            {(MNMsectional && (products.length >= 1 || collections.length >= 1)) ||
            (mixmatch_product_selection?.value === "collection" && collections && collections?.length > 0) ? (
                <div className={styles.card} style={{ marginTop: 10 }}>
                    {/* Checkbox */}
                    <div className={styles.checkboxContainer}>
                        <div className={styles.customLabelWrapper}>
                            <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                                <Label id="bundle_product_level">Visibility</Label>
                                <div style={{ marginTop: "5px" }}>Show bundle widget on</div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    marginTop: "-2px",
                                    marginLeft: "10px",
                                }}
                            >
                                <RadioButton
                                    label={"Included product pages"}
                                    id={"all pages"}
                                    checked={
                                        render_product_pages_type.value == true &&
                                        !render_on_selected_collection_products?.value
                                    }
                                    name="bundle_position"
                                    onChange={() => {
                                        dispatchBundleConfig({
                                            payload: true,
                                            key: "render_product_pages_type",
                                        });
                                        dispatchBundleConfig({
                                            payload: [],
                                            key: "allowed_product_pages",
                                        });
                                        render_product_pages_type.onChange(true);
                                        validProductPages && validProductPages.onChange([]);
                                        if (
                                            bundleType == BundleTypes.MIX_MATCH ||
                                            bundleType == BundleTypes.MIX_MATCH_SECTIONAL
                                        ) {
                                            dispatchBundleConfig({
                                                payload: false,
                                                key: "render_on_selected_collection_products" as any,
                                            });
                                            dispatchBundleConfig({
                                                payload: [] as ProductObj[],
                                                key: "allowed_collection_product_pages" as any,
                                            });
                                            render_on_selected_collection_products?.onChange(false);
                                            allowed_collection_product_pages?.onChange([]);
                                        }
                                    }}
                                />
                                {(bundleType == BundleTypes.MIX_MATCH ||
                                    (bundleType == BundleTypes.MIX_MATCH_SECTIONAL &&
                                        mixmatch_product_selection?.value === "collection")) && (
                                    <>
                                        <RadioButton
                                            label={"Specific product pages"}
                                            id={"specific_collection_pages"}
                                            checked={
                                                render_product_pages_type.value == true &&
                                                render_on_selected_collection_products?.value == true
                                            }
                                            name="bundle_position"
                                            onChange={() => {
                                                dispatchBundleConfig({
                                                    payload: true,
                                                    key: "render_product_pages_type",
                                                });
                                                dispatchBundleConfig({
                                                    payload: true,
                                                    key: "render_on_selected_collection_products" as any,
                                                });
                                                if (
                                                    !allowed_collection_product_pages?.value ||
                                                    allowed_collection_product_pages.value.length === 0
                                                ) {
                                                    dispatchBundleConfig({
                                                        payload: [] as ProductObj[],
                                                        key: "allowed_collection_product_pages" as any,
                                                    });
                                                    allowed_collection_product_pages?.onChange([]);
                                                }
                                                render_product_pages_type.onChange(true);
                                                render_on_selected_collection_products?.onChange(true);
                                            }}
                                        />
                                        {render_product_pages_type.value == true &&
                                            render_on_selected_collection_products?.value == true &&
                                            mixmatch_product_selection?.value === "collection" && (
                                                <div style={{ paddingLeft: "20px" }}>
                                                    <CollectionProductSelector
                                                        collectionIds={collections.map((c) => c.id)}
                                                        selectedProducts={selectedCollectionProducts}
                                                        onProductsChange={handleCollectionProductsChange}
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                                <RadioButton
                                    label={
                                        <span
                                            style={{
                                                display: "flex",
                                                justifyContent: "flex-start",
                                                alignItems: "center",
                                                gap: 5,
                                            }}
                                        >
                                            No product pages
                                            <Tooltip
                                                width="wide"
                                                content={
                                                    "The discounts will be automatically applied at cart and checkout once the customer meets the required criteria."
                                                }
                                            >
                                                <Icon source={InfoIcon} tone="subdued" />
                                            </Tooltip>
                                        </span>
                                    }
                                    id={"none"}
                                    name="bundle_position"
                                    checked={render_product_pages_type.value == false}
                                    onChange={() => {
                                        dispatchBundleConfig({
                                            payload: false,
                                            key: "render_product_pages_type",
                                        });
                                        dispatchBundleConfig({
                                            payload: [],
                                            key: "allowed_product_pages",
                                        });
                                        render_product_pages_type.onChange(false);
                                        validProductPages && validProductPages.onChange([]);
                                        if (
                                            bundleType == BundleTypes.MIX_MATCH ||
                                            bundleType == BundleTypes.MIX_MATCH_SECTIONAL
                                        ) {
                                            dispatchBundleConfig({
                                                payload: false,
                                                key: "render_on_selected_collection_products" as any,
                                            });
                                            dispatchBundleConfig({
                                                payload: [] as ProductObj[],
                                                key: "allowed_collection_product_pages" as any,
                                            });
                                            render_on_selected_collection_products?.onChange(false);
                                            allowed_collection_product_pages?.onChange([]);
                                        }
                                    }}
                                />
                            </div>
                            {/* {render_product_pages_type.value == true && (
                                <div className={styles.tagContainer} key={updateKey}>
                                    {Array.from(new Set(allProductIds)).map((id) => {
                                        const product = products.find((product) => product.id === id);
                                        return (
                                            <Checkbox
                                                onChange={() => {
                                                    updateArrayBasedOnCheckbox(product?.id);
                                                }}
                                                value={product?.id as string}
                                                checked={!!selectedProductIds.includes(product?.id as string)}
                                                label={product?.title || "Unknown Product"}
                                                key={product?.id}
                                            />
                                        );
                                    })}
                                </div>
                            )} */}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default AllowedProductPages;
